import { useCallback, useEffect, useRef, useState } from 'react';
import { bomClient } from '../client/bomClient';
import { BOMStatusResponse, ComponentResearchLive, ResearchSummary } from '../types/bom.types';

/**
 * The BOM status poll behind steps 3a–3c. Research results arrive by push (the
 * research engine's callback marks each component server-side); this poll only
 * notices that they landed, so it starts responsive and relaxes while nothing
 * changes. A hidden tab keeps a slow beat instead of stopping, so a buyer who
 * comes back after an hour sees the truth at once rather than a stale board.
 */
export const POLL_MIN_MS = 3000;
export const POLL_MAX_MS = 30000;
export const POLL_HIDDEN_MS = 60000;
const POLL_BACKOFF = 1.6;

type StatusComponent = BOMStatusResponse['components'][number];

const statusOf = (c: StatusComponent): string => String(c.researchMetadata?.status || c.status || '').toUpperCase();
const isFinished = (c: StatusComponent) => statusOf(c) === 'COMPLETED' || statusOf(c) === 'FAILED';

type ContactFlags = { hasContactEmail?: boolean; findingContact?: boolean };
const candidatesOf = (c: StatusComponent): ContactFlags[] => (c.supplierCandidates as ContactFlags[] | undefined) ?? [];

/** Robbie is still looking up a supplier's contact: research is done, the board is not. */
const isFindingContacts = (c: StatusComponent) => candidatesOf(c).some((s) => s.findingContact === true);

/**
 * Every per-component fact the board renders. Any change here refetches the
 * RFQ — a part that fails or finds nothing never moves `processedComponents`,
 * so keying on that counter alone left such parts showing "searching".
 */
export const researchSignatureOf = (components: StatusComponent[]): string =>
  components
    .map((c) => {
      const id = String(c._id ?? (c as { id?: string }).id ?? '');
      const suppliers = (c as { supplierCount?: number }).supplierCount ?? c.supplierCandidates?.length ?? 0;
      // A contact lookup landing changes no status, only who has an email and who is still being looked up.
      const candidates = candidatesOf(c);
      const contacts = `${candidates.filter((s) => s.hasContactEmail).length}/${candidates.filter((s) => s.findingContact).length}`;
      return `${id}:${statusOf(c)}:${c.researchState ?? ''}:${c.attempt ?? ''}:${suppliers}:${contacts}`;
    })
    .join('|');

const liveOf = (components: StatusComponent[]): Record<string, ComponentResearchLive> => {
  const live: Record<string, ComponentResearchLive> = {};
  for (const c of components) {
    const entry: ComponentResearchLive = {
      queued: c.queued,
      researchStage: c.researchStage,
      knownSuppliers: c.knownSuppliers,
      researchState: c.researchState,
      attempt: c.attempt,
      maxAttempts: c.maxAttempts,
      startsInMinutes: c.startsInMinutes,
    };
    if (c._id) live[String(c._id)] = entry;
    const id = (c as { id?: string }).id;
    if (id) live[id] = entry;
  }
  return live;
};

interface Options {
  rfqId?: string;
  refreshRfq: (rfqId: string) => Promise<unknown>;
  /** The robotics analysis landed (3a/3b need it). */
  onAnalysis: (analysis: Awaited<ReturnType<typeof bomClient.getAnalysisStatus>>) => void;
  /** The analysis run persisted a failure and will never progress. */
  onAnalysisFailed: (message: string) => void;
  /** Still running: when the server says it started (null if it does not say). */
  onAnalysisRunning?: (runningSince: string | null) => void;
}

export const useBomStatusPoll = ({ rfqId, refreshRfq, onAnalysis, onAnalysisFailed, onAnalysisRunning }: Options) => {
  const [liveResearch, setLiveResearch] = useState<Record<string, ComponentResearchLive>>({});
  const [research, setResearch] = useState<ResearchSummary | null>(null);
  const stopRef = useRef<(() => void) | null>(null);
  // Fresh callbacks for the running loop without restarting it on every render.
  const cb = useRef({ rfqId, refreshRfq, onAnalysis, onAnalysisFailed, onAnalysisRunning });
  cb.current = { rfqId, refreshRfq, onAnalysis, onAnalysisFailed, onAnalysisRunning };

  const start = useCallback((bomId: string) => {
    // A new call is always a user action (mount, retry, research kickoff), so
    // it replaces any running loop and starts back at the responsive rate.
    stopRef.current?.();

    let analysisReceived = false;
    let lastSignature: string | null = null;
    let delay = POLL_MIN_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    let running = false;

    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const stop = () => {
      stopped = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (stopRef.current === stop) stopRef.current = null;
    };
    const schedule = () => {
      if (stopped) return;
      clearTimer();
      timer = setTimeout(poll, document.visibilityState === 'hidden' ? POLL_HIDDEN_MS : delay);
    };
    // Coming back is a user action: answer at once, at the responsive rate.
    function onVisibilityChange() {
      if (stopped) return;
      if (document.visibilityState === 'hidden') {
        schedule();
        return;
      }
      delay = POLL_MIN_MS;
      clearTimer();
      poll();
    }

    const fetchAnalysis = async (allDone: boolean) => {
      try {
        const analysis = await bomClient.getAnalysisStatus(bomId);
        // The run marker outranks the stored result: a re-run is RUNNING
        // while the last stored result is still the failed one.
        const marker = analysis?.analysisRun?.status;
        const pending = analysis?.status === 'pending' || analysis?.status === 'PENDING';
        if (marker === 'RUNNING' || (pending && marker !== 'FAILED')) {
          cb.current.onAnalysisRunning?.(analysis?.analysisRun?.runningSince ?? null);
          return;
        }
        if (analysis?.currentStage === 'failed' || marker === 'FAILED') {
          cb.current.onAnalysisFailed(analysis.error || 'BOM analysis failed. Please re-upload or try again.');
          stop();
          return;
        }
        cb.current.onAnalysis(analysis);
        analysisReceived = true;
        if (allDone) stop();
      } catch {
        // Transient: the next tick asks again.
      }
    };

    async function poll() {
      timer = null;
      if (running) return;
      running = true;
      let progressed = false;
      try {
        const status = await bomClient.getBOMStatus(bomId);
        const components = status.components || [];
        setLiveResearch(liveOf(components));
        setResearch(status.research ?? null);
        const allDone = components.length > 0 && components.every(isFinished) && !components.some(isFindingContacts);
        const signature = researchSignatureOf(components);
        progressed = signature !== lastSignature;
        lastSignature = signature;
        const { rfqId: id, refreshRfq: refresh } = cb.current;
        if (id && (progressed || allDone)) {
          // preserveStep inside refresh: a background poll never moves the wizard.
          await refresh(id).catch(() => undefined);
        }
        if (!analysisReceived) await fetchAnalysis(allDone);
        else if (allDone) stop();
      } catch {
        // A failed tick is retried on the next one.
      }
      running = false;
      delay = progressed ? POLL_MIN_MS : Math.min(Math.round(delay * POLL_BACKOFF), POLL_MAX_MS);
      schedule();
    }

    stopRef.current = stop;
    document.addEventListener('visibilitychange', onVisibilityChange);
    poll();
  }, []);

  useEffect(() => () => stopRef.current?.(), []);

  return { liveResearch, research, start };
};

export default useBomStatusPoll;
